import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Container,
  Table,
  Form,
  Badge,
  Modal,
  Alert,
} from "react-bootstrap";
import {
  FaPlus,
  FaCheckCircle,
  FaTimes,
  FaUsers,
  FaPauseCircle,
  FaEye,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaCalendarCheck,
  FaBuilding,
  FaBriefcase
} from "react-icons/fa";
import { AppDispatch, RootState } from "../../store";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import usersService from "../../features/users/usersService";
import { User, Role } from "../../features/users/usersSlice";
import UserFormModal from "../../components/users/UserFormModal";
import Swal from 'sweetalert2';
import Pagination from "../../components/common/Pagination";
import companiesService, { Company } from "../../features/companies/companiesService";
import { isCompanyOwner } from "../../utils/permissions";

const MyTeamPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);

  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [agencyId, setAgencyId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);

  // Team members'ları yükle
  useEffect(() => {
    loadOwnerContext();
    loadRoles();
  }, []);

  useEffect(() => {
    if (agencyId || companyId) {
      loadTeamMembers();
    }
  }, [agencyId, companyId]);

  const loadOwnerContext = async () => {
    try {
      if (isCompanyOwner(user)) {
        const myCompany = await companiesService.getMyCompany();
        setCompanyId(myCompany.id);
        setCompanies([myCompany as Company]);
      } else {
        // Agency Owner context
        const allCompanies = await companiesService.getMyAgencyCompanies();
        const agency = allCompanies.find((company: any) => company.company_type === "agency");
        if (agency) {
          setAgencyId(agency.id);
          setCompanies(allCompanies);
        }
      }
    } catch (err) {
      console.error("Error loading owner context:", err);
      setError("Failed to load owner context. Please try again.");
    }
  };

  const loadTeamMembers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (isCompanyOwner(user)) {
        if (!companyId) return;
        const members = await companiesService.getCompanyUsers(companyId);
        // company_owner da listede görünsün; id alanını normalize et
        const filtered = (members || [])
          .filter((m: any) =>
            Array.isArray(m.roles) && m.roles.some((r: any) => {
              const key = r.key || r;
              return key === "company_specialist" || key === "company_owner" || key === "personal";
            })
          )
          .map((m: any) => ({ ...m, id: m.user_id }));
        setTeamMembers(filtered);
      } else {
        if (!agencyId) return;
        const members = await usersService.getAgencyUsers(agencyId);
        setTeamMembers(members);
      }
    } catch (err) {
      console.error("Error loading team members:", err);
      setError("Failed to load team members. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const allRoles = await usersService.getRoles();
      let relevantRoles: Role[] = [];
      if (isCompanyOwner(user)) {
        relevantRoles = allRoles.filter((role: Role) =>
          role.key === "company_specialist" || role.key === "personal"
        );
      } else {
        relevantRoles = allRoles.filter((role: Role) =>
          role.key === "marketing_specialist" || role.key === "agency_owner"
        );
      }
      setRoles(relevantRoles);
    } catch (err) {
      console.error("Error loading roles:", err);
    }
  };

  const handleModalHide = (refresh?: boolean) => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDetailModal(false);
    setSelectedUser(null);
    if (refresh) {
      loadTeamMembers();
    }
  };

  const handleViewUser = async (usr: User) => {
    try {
      let userDetail: any;
      if (isCompanyOwner(user)) {
        userDetail = await usersService.getUserById(usr.id);
      } else {
        if (!agencyId) return;
        userDetail = await usersService.getAgencyUserDetail(agencyId, usr.id);
      }
      setSelectedUser(userDetail);
      setShowDetailModal(true);
    } catch (err) {
      console.error("Error loading user details:", err);
      toast.error("Failed to load user details");
    }
  };

  const handleEditUser = async (usr: User) => {
    try {
      let userDetail: any;
      if (isCompanyOwner(user)) {
        userDetail = await usersService.getUserById(usr.id);
      } else {
        if (!agencyId) return;
        userDetail = await usersService.getAgencyUserDetail(agencyId, usr.id);
      }
      setSelectedUser(userDetail);
      setShowEditModal(true);
    } catch (err) {
      console.error("Error loading user details:", err);
      toast.error("Failed to load user details");
    }
  };

  const handleDeleteUser = async (usr: User) => {
    const result = await Swal.fire({
      title: 'Delete Team Member',
      text: `Are you sure you want to delete ${usr.full_name || usr.username}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      // Loading göster
      Swal.fire({
        title: 'Deleting User...',
        text: 'Please wait while we delete the user.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      if (isCompanyOwner(user)) {
        await usersService.deleteUser(usr.id);
      } else {
        if (!agencyId) return;
        await usersService.deleteAgencyUser(agencyId, usr.id);
      }
      loadTeamMembers();

      // Başarı mesajı göster
      Swal.fire({
        title: 'Success!',
        text: `${usr.full_name || usr.username} has been deleted successfully.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error("Error deleting user:", err);

      // Hata mesajı göster
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete user. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const s = status?.toLowerCase();
    switch (s) {
      case "active":
        return (
          <span className="status-badge-modern success">
            <Icon icon={FaCheckCircle} className="me-1" />
            {t("active")}
          </span>
        );
      case "suspended":
        return (
          <span className="status-badge-modern danger">
            <Icon icon={FaTimes} className="me-1" />
            {t("suspended")}
          </span>
        );
      default:
        return (
          <span className="status-badge-modern secondary">
            <Icon icon={FaPauseCircle} className="me-1" />
            {t("inactive")}
          </span>
        );
    }
  };

  const getRoleBadge = (roles: any[]) => {
    return (roles || []).map((r: any, index: number) => {
      const key: string = (r?.key || r || '').toString();
      const label: string = r?.name || key;
      return (
        <span key={index} className="badge bg-soft-primary text-primary me-1">
          {label}
        </span>
      );
    });
  };

  // Pagination
  const paginatedMembers = teamMembers.slice((page - 1) * limit, page * limit);
  const totalCount = teamMembers.length;
  const totalPages = Math.ceil(totalCount / limit);
  const activeCount = teamMembers.filter((m) => m.status === "active").length;
  const inactiveCount = totalCount - activeCount;

  if (isLoading) {
    return (
      <div className="agency-page-layout">
        <Container fluid>
          <div className="modern-loading-state">
            <div className="loading-spinner">
              <Icon icon={FaUsers} className="fa-spin" />
            </div>
            <h5 className="loading-title">Loading team members...</h5>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="agency-page-layout">
      <Container fluid>
        {/* Modern Header */}
        <div className="modern-header slide-in-down">
          <div className="header-content">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <div className="header-icon">
                  <Icon icon={FaUsers} />
                </div>
              </div>
              <div className="header-text">
                <h1 className="header-title">My Team</h1>
                <p className="header-subtitle">Manage your team members and their roles</p>
              </div>
            </div>
            <div className="header-actions">
              <Button
                className="btn-modern btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <Icon icon={FaPlus} className="me-2" />
                <span>{t("add_member")}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Modern Stats Grid */}
        <div className="modern-stats-grid slide-in-up animate-delay-1">
          <div className="stat-modern-card">
            <div className="stat-modern-icon primary">
              <Icon icon={FaUsers} />
            </div>
            <div className="stat-modern-content">
              <div className="stat-modern-value">{totalCount}</div>
              <div className="stat-modern-label">Total Members</div>
            </div>
          </div>

          <div className="stat-modern-card">
            <div className="stat-modern-icon active">
              <Icon icon={FaCheckCircle} />
            </div>
            <div className="stat-modern-content">
              <div className="stat-modern-value">{activeCount}</div>
              <div className="stat-modern-label">Active Members</div>
            </div>
          </div>

          <div className="stat-modern-card">
            <div className="stat-modern-icon inactive">
              <Icon icon={FaPauseCircle} />
            </div>
            <div className="stat-modern-content">
              <div className="stat-modern-value">{inactiveCount}</div>
              <div className="stat-modern-label">Inactive Members</div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Modern Team Members Table */}
        <div className="modern-table-container slide-in-up animate-delay-2">
          <div className="table-header-modern">
            <div className="table-header-left">
              <h3 className="table-title">Team Members</h3>
              <span className="table-subtitle">{totalCount} members total</span>
            </div>
            <div className="table-header-right">
              <Form.Select
                size="sm"
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                className="modern-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </Form.Select>
            </div>
          </div>
          <div className="table-content-modern">
            {paginatedMembers.length === 0 ? (
              <div className="modern-empty-state">
                <div className="empty-icon-modern">
                  <Icon icon={FaUsers} />
                </div>
                <h4 className="empty-title-modern">No Team Members</h4>
                <p className="empty-description-modern">
                  Start building your team by adding the first member
                </p>
                <Button
                  className="btn-modern btn-primary mt-3"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Icon icon={FaPlus} className="me-2" />
                  <span>Add First Member</span>
                </Button>
              </div>
            ) : (
              <div className="modern-table-wrapper table-responsive">
                <Table className="modern-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}></th>
                      <th>{t("name")}</th>
                      <th>{t("email")}</th>
                      <th>{t("roles")}</th>
                      <th>{t("status")}</th>
                      <th style={{ width: 120 }}>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMembers.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <div className="avatar-modern circle">
                            <Icon icon={FaUsers} />
                          </div>
                        </td>
                        <td>
                          <div className="info-cell-modern">
                            <div className="cell-title">{member.full_name || member.username}</div>
                            <div className="cell-subtitle">@{member.username}</div>
                          </div>
                        </td>
                        <td>
                          <div className="user-email-modern">
                            <a href={`mailto:${member.email}`} className="text-decoration-none text-reset">
                              {member.email}
                            </a>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {getRoleBadge(member.roles)}
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(member.status)}
                        </td>
                        <td>
                          <div className="action-buttons-modern">
                            <button
                              className="action-btn-modern view"
                              onClick={() => handleViewUser(member)}
                              title="View Details"
                              aria-label="View Details"
                            >
                              <Icon icon={FaEye} />
                            </button>
                            <button
                              className="action-btn-modern edit"
                              onClick={() => handleEditUser(member)}
                              title="Edit User"
                              aria-label="Edit User"
                            >
                              <Icon icon={FaEdit} />
                            </button>
                            <button
                              className="action-btn-modern delete"
                              onClick={() => handleDeleteUser(member)}
                              title="Delete User"
                              aria-label="Delete User"
                            >
                              <Icon icon={FaTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>

          {/* Modern Pagination */}
          {totalCount > 0 && totalPages > 1 && (
            <div className="modern-pagination">
              <div className="pagination-info-modern">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount} members
              </div>
              <Pagination
                currentPage={page}
                totalCount={totalCount}
                pageSize={limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>

        {/* User Detail Modal - Modern Style */}
        <Modal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          size="lg"
          centered
          scrollable
          fullscreen="sm-down"
          className="modern-modal user-details-modal"
          backdrop="static"
        >
          <Modal.Header closeButton className="modern-modal-header border-0 pb-0">
            <div className="modal-header-content">
              <div className="modal-header-left">
                <div className="modal-header-icon bg-soft-primary text-primary">
                  <Icon icon={FaUsers} />
                </div>
                <div className="modal-header-text">
                  <h4 className="modal-title">{t("user_details")}</h4>
                  <p className="modal-subtitle">{t("view_user_profile")}</p>
                </div>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body className="modern-modal-body pt-0">
            {selectedUser && (
              <div className="user-profile-layout">
                {/* Profile Overview Section */}
                <div className="profile-overview-card mb-4 mt-3">
                  <div className="profile-main-info">
                    <div className="avatar-wrapper">
                      <div className="avatar-modern circle profile-avatar">
                        <span>
                          {selectedUser.full_name
                            ? selectedUser.full_name.substring(0, 2).toUpperCase()
                            : selectedUser.username.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div className="status-indicator-wrapper">
                        <span className={`status-indicator ${selectedUser.status}`}></span>
                      </div>
                    </div>

                    <div className="profile-text-info">
                      <h2 className="user-full-name mb-1">
                        {selectedUser.full_name || selectedUser.username}
                      </h2>
                      <div className="user-meta-tags">
                        <span className="username-tag">@{selectedUser.username}</span>
                        <div className="roles-list">
                          {getRoleBadge(selectedUser.roles)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="profile-status-badge">
                    {getStatusBadge(selectedUser.status)}
                  </div>
                </div>

                {/* Contact & General Info Cards */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6 col-12">
                    <div className="info-modern-card">
                      <div className="card-icon">
                        <Icon icon={FaEnvelope} />
                      </div>
                      <div className="card-content">
                        <div className="card-label">{t("email_address")}</div>
                        <a href={`mailto:${selectedUser.email}`} className="card-value email-link">
                          {selectedUser.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 col-12">
                    <div className="info-modern-card">
                      <div className="card-icon">
                        <Icon icon={FaCalendarCheck} />
                      </div>
                      <div className="card-content">
                        <div className="card-label">{t("joined_date")}</div>
                        <div className="card-value">
                          {new Date(selectedUser.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Assigned Companies Section */}
                <div className="assignments-section">
                  <div className="section-header-modern mb-3">
                    <div className="section-header-left">
                      <h5 className="section-title">
                        <Icon icon={FaBuilding} className="me-2 text-primary" />
                        {t("assigned_companies")}
                      </h5>
                    </div>
                    <Badge bg="soft-primary" className="text-primary rounded-pill px-3">
                      {selectedUser.companies?.length || 0} {t("companies")}
                    </Badge>
                  </div>

                  <div className="companies-modern-grid">
                    {selectedUser.companies && selectedUser.companies.length > 0 ? (
                      selectedUser.companies.map((comp: any, idx: number) => (
                        <div key={idx} className="company-assignment-item">
                          <div className="company-info-main">
                            <div className="company-logo-mini">
                              <Icon icon={FaBuilding} />
                            </div>
                            <div className="company-text">
                              <div className="company-name">{comp.company_name || `Company ${comp.company_id}`}</div>
                              <div className="company-type-label text-muted small">
                                {comp.company_type || t("client_company")}
                              </div>
                            </div>
                          </div>
                          <div className="role-assignment-tag">
                            <Icon icon={FaBriefcase} className="me-1" />
                            {comp.role || t("member")}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-assignments text-center p-4 rounded border-dashed">
                        <Icon icon={FaBuilding} className="text-muted fs-2 mb-2" />
                        <p className="text-muted mb-0">{t("no_companies_assigned")}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-center">
            <Button
              variant="outline-secondary"
              className="btn-minimal px-4 py-2"
              size="sm"
              onClick={() => setShowDetailModal(false)}
            >
              <Icon icon={FaTimes} className="me-2" />
              <span>{t("close")}</span>
            </Button>
          </Modal.Footer>
        </Modal>

        {/* User Form Modal */}
        <UserFormModal
          show={showCreateModal || showEditModal}
          onHide={handleModalHide}
          user={selectedUser}
          roles={roles}
          companies={companies}
          agencyId={agencyId || undefined}
        />
      </Container>
    </div>
  );
};

export default MyTeamPage;
